// Cook Mode — full-viewport distraction-free interface.
//
//   Header: swatch + title + step counter + exit X
//   Left:   checkable ingredients (strike-through on check, local state)
//   Center: large step text + (future) minutes timer
//   Bottom: Previous / progress pips / Next  (or "Done — eat" on last step)
//
// Keyboard: ← / → navigate, Esc exits.

import { useEffect, useMemo, useState } from "preact/hooks";
import { Icon, hueGrad } from "../../icons";
import { api, type Recipe, type RecipeItem, type MealSlot, type EffectiveMealItem } from "../../lib/rpc";
// (RecipeItem is still imported for the .map type annotation in the
// fetch branch where r.items has the original shape.)
import { hueFromId } from "../../lib/use-recipes";
import { usePantry } from "../../lib/use-pantry";
import { scaleQuantity } from "../../lib/recipe-scale";
import { CookDoneSweep } from "./cook-done-sweep";
import { CookTimerDock } from "./cook-timer-dock";
import type { NotifyFn } from "../../components/toast";

const SCALE_OPTIONS = [0.5, 1, 1.5, 2, 3, 4];

/** Lowest-common-denominator item type so we can feed either a plain
 *  recipe or a meal-plan-overlaid effective-item list into the same UI.
 *  RecipeItem ids are strings; EffectiveMealItem ids are numbers — we
 *  string-coerce the latter on ingest so the `checked` set stays
 *  string-keyed. */
interface CookItem {
  id: string;
  text: string;
  quantity: string | null;
  emoji: string | null;
}

interface CookModeProps {
  recipeId: string;
  scale: number;
  onScaleChange: (scale: number) => void;
  onClose: () => void;
  /** Same auto-scale plumbing as RecipeDetail. We don't apply auto
   *  on mount here — the parent already did when opening the detail
   *  panel, and the scale prop carries that pick into cook mode. */
  householdSize?: number | null;
  /** Required for the post-cook "any left?" sweep — surfaces a toast
   *  with the summary of pantry changes after commit. */
  notify: NotifyFn;
  /** When cooking a meal-plan entry, this carries the (date, slot)
   *  reference. CookMode then resolves ingredients via
   *  mealplanGetEffectiveItems — applying any swaps / removals /
   *  additions the user made on that specific day — rather than
   *  reading the canonical recipe. Steps still come from the recipe
   *  (the overlay only mutates ingredients today). */
  entryRef?: { date: string; slot: MealSlot } | null;
}

export function CookMode({ recipeId, scale, onScaleChange, onClose, householdSize = null, notify, entryRef = null }: CookModeProps) {
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [items, setItems] = useState<CookItem[]>([]);
  const [step, setStep] = useState(0);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [sweepOpen, setSweepOpen] = useState(false);
  // usePantry is fetched lazily — only matters when the user hits
  // "Done — eat" and we decide whether to open the sweep. Pre-loading
  // here keeps that decision instant (no spinner on the last step).
  const pantry = usePantry();

  // Build the post-cook sweep rows. EVERY recipe ingredient gets a row;
  // ones that already exist in the pantry (case-insensitive name match)
  // carry the pantryItem so the update path can fire on commit, ones
  // that don't get pantryItem=null so the sweep can offer to start
  // tracking them (the user just cooked with it — they had it).
  const sweepRows = useMemo(() => {
    if (items.length === 0) return [];
    const byName = new Map<string, typeof pantry.items[number]>();
    for (const p of pantry.items) byName.set(p.name.toLowerCase().trim(), p);
    return items.map((it) => ({
      recipeItem: { text: it.text, quantity: it.quantity },
      pantryItem: byName.get((it.text || "").toLowerCase().trim()) || null,
    }));
  }, [items, pantry.items]);

  const handleDone = () => {
    // Always open the sweep (when there are recipe items at all) — even
    // with zero pantry matches, the user can record what they used and
    // unmatched picks turn into new pantry rows on commit.
    if (sweepRows.length === 0) {
      onClose();
      return;
    }
    setSweepOpen(true);
  };

  useEffect(() => {
    let cancelled = false;
    // Two data paths:
    //   1. entryRef present (cooking a meal-plan day): fetch the
    //      effective items via mealplanGetEffectiveItems so any per-day
    //      swaps / removals / additions are reflected. Then fetch the
    //      base recipe for name / image / steps (the overlay only
    //      mutates ingredients today).
    //   2. No entryRef: cooking the canonical recipe directly. Just
    //      pull recipe + its items.
    const load = async () => {
      if (entryRef) {
        const [eff, r] = await Promise.all([
          api.mealplanGetEffectiveItems({ date: entryRef.date, slot: entryRef.slot }),
          api.recipesGet(recipeId),
        ]);
        if (cancelled) return;
        setRecipe(r.recipe);
        // String-coerce the numeric EffectiveMealItem.id so it slots
        // into the same Set<string> the checkbox tracks.
        setItems(eff.items.map((it: EffectiveMealItem) => ({
          id: `eff:${it.id}`,
          text: it.text,
          quantity: it.quantity,
          emoji: it.emoji,
        })));
      } else {
        const r = await api.recipesGet(recipeId);
        if (cancelled) return;
        setRecipe(r.recipe);
        setItems(r.items.map((it: RecipeItem) => ({
          id: it.id,
          text: it.text,
          quantity: it.quantity,
          emoji: it.emoji ?? null,
        })));
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [recipeId, entryRef?.date, entryRef?.slot]);

  const steps = useMemo(() => (
    Array.isArray(recipe?.instructions) ? recipe!.instructions : []
  ), [recipe]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && step > 0) setStep(step - 1);
      else if (e.key === "ArrowRight" && step < steps.length - 1) setStep(step + 1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [step, steps.length, onClose]);

  const toggleCheck = (id: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const last = steps.length > 0 && step === steps.length - 1;

  return (
    <div className="cook-mode">
      <header className="cook-head">
        <div
          className="cook-head-swatch"
          style={recipe?.imageUrl ? undefined : { background: hueGrad(hueFromId(recipeId)) }}
        >
          {recipe?.imageUrl && <img src={recipe.imageUrl} alt="" />}
        </div>
        <div className="cook-head-title">{recipe?.name || "Cooking"}</div>
        {steps.length > 0 && (
          <span className="cook-head-counter">
            Step {step + 1} of {steps.length}
          </span>
        )}
        <div className="cook-head-scale" role="radiogroup" aria-label="Scale">
          {/* Auto-scale chip mirrors the detail panel — only render
              when household + serves yield a factor that isn't an
              existing preset. The detail panel applies auto on open
              so the active scale is usually already this value when
              cook mode launches. */}
          {(() => {
            const auto = (recipe?.serves && householdSize && recipe.serves > 0)
              ? Math.round((householdSize / recipe.serves) * 100) / 100
              : null;
            const autoMatchesPreset = auto != null && SCALE_OPTIONS.some((s) => s === auto);
            if (auto == null || autoMatchesPreset) return null;
            return (
              <button
                type="button"
                role="radio"
                aria-checked={scale === auto}
                className={`cook-head-scale-chip auto${scale === auto ? " active" : ""}`}
                onClick={() => onScaleChange(auto)}
                title={`Feeds ${householdSize}`}
              >
                ×{auto}
              </button>
            );
          })()}
          {SCALE_OPTIONS.map((s) => (
            <button
              key={s}
              type="button"
              role="radio"
              aria-checked={scale === s}
              className={`cook-head-scale-chip${scale === s ? " active" : ""}`}
              onClick={() => onScaleChange(s)}
            >
              ×{s}
            </button>
          ))}
        </div>
        <button className="icon-btn" onClick={onClose} title="Exit (Esc)" type="button">
          <Icon name="x" size={14} />
        </button>
      </header>

      {/* Hovering timer dock — pinned just under the header so it stays
        * visible across every step. Lists active timers with live
        * countdown, parses the current step text for one-tap "Start Nm"
        * suggestions, and exposes a "+ Timer" input for custom
        * durations. Replaces the per-step inline StepTimers row. */}
      {steps.length > 0 && (
        <CookTimerDock
          recipeName={recipe?.name || "Recipe"}
          stepNum={step + 1}
          stepText={steps[step]}
        />
      )}

      <div className="cook-body">
        <div className="cook-ingredients">
          <div className="cook-ingredients-label">Ingredients</div>
          {items.length === 0
            ? <p style={{ color: "var(--text-dim)", fontStyle: "italic" }}>None set.</p>
            : items.map((it) => (
                <div
                  key={it.id}
                  className="cook-ingredient"
                  data-checked={checked.has(it.id)}
                  onClick={() => toggleCheck(it.id)}
                >
                  <span
                    className="todo-cbx"
                    data-checked={checked.has(it.id)}
                    style={{ width: 18, height: 18 }}
                    aria-hidden
                  >
                    <Icon name="check" size={11} />
                  </span>
                  {it.emoji && <span style={{ width: 16 }}>{it.emoji}</span>}
                  <span className="cook-ingredient-text">{it.text}</span>
                  {it.quantity && (
                    <span className="cook-ingredient-qty">{scaleQuantity(it.quantity, scale)}</span>
                  )}
                </div>
              ))
          }
        </div>

        <div className="cook-step">
          {steps.length === 0
            ? (
              <p style={{ color: "var(--text-mute)", fontStyle: "italic" }}>
                This recipe doesn't have any steps yet — add some in the detail view.
              </p>
            )
            : (
              <>
                <div className="cook-step-title">Step {step + 1}</div>
                <div className="cook-step-text">{steps[step]}</div>
                {/* Timers moved to the hovering dock at the top of cook
                  * mode — gives them a fixed home that's visible no
                  * matter which step you're on. */}
              </>
            )
          }
        </div>
      </div>

      <footer className="cook-foot">
        <button
          className="btn"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
          type="button"
        >
          <Icon name="arrow-left" size={14} />
          <span>Previous</span>
        </button>
        <div className="cook-pips" aria-hidden>
          {steps.map((_, i) => (
            <span
              key={i}
              className="cook-pip"
              data-active={i === step}
              data-done={i < step}
            />
          ))}
        </div>
        {last ? (
          <button className="btn btn-primary" onClick={handleDone} type="button">
            <Icon name="check" size={14} />
            <span>Done — eat</span>
          </button>
        ) : (
          <button
            className="btn btn-primary"
            onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))}
            disabled={steps.length === 0 || step >= steps.length - 1}
            type="button"
          >
            <span>Next</span>
            <Icon name="arrow-right" size={14} />
          </button>
        )}
      </footer>

      {sweepOpen && (
        <CookDoneSweep
          matches={sweepRows}
          onClose={() => { setSweepOpen(false); onClose(); }}
          onCommitted={() => { setSweepOpen(false); onClose(); }}
          notify={notify}
        />
      )}
    </div>
  );
}

